/**
 * Brazil country pack — the first Vello country pack, expressed as **data** over the universal
 * `@vello/config` (S007 country/market shape, S008 policy resolution) and `@vello/core` (S00x money)
 * contracts. Nothing here is a universal rule: every Brazil-specific symbol (`BRL`, `pt-BR`, `Pix`,
 * `CPF`/`CNH`/`CRLV`, calling code `55`, `America/Sao_Paulo`) lives in this pack tree and nowhere in the
 * shared kernel/services. `scripts/check-neutrality.mjs` enforces that boundary (ADR-0005, ADR-0013).
 *
 * Boundary with S011 (ADR-0013): this pack declares *which* documents Brazil requires — as typed
 * references — and *which* instruments provide which universal payment capability. It does **not**
 * implement CPF/CNPJ/CEP validation or BRL/date/number formatting; those adapters are S010/S011. Keeping
 * the reference here and the validator there is deliberate: adding a validator would absorb S011's outcome.
 */
import { countryCode, countryPack, marketId, marketLaunchState, operatingMarket, policySetting, regionCode, } from '@vello/config';
import { moneyFromSafeInteger } from '@vello/core';
import { brazilPilotPaymentMethodSettings } from './payment-methods.js';
import { brazilPartnerDeskSettings } from './partner-desk.js';
/** ISO 3166-1 alpha-2 for Brazil. The one place `BR` is minted as a country identity for the pack. */
export const BRAZIL = countryCode('BR');
/** Brazil's ISO 4217 currency and its minor-unit (centavo) precision. */
export const BRAZIL_CURRENCY = 'BRL';
export const BRAZIL_CURRENCY_MINOR_UNITS = 2;
/** Brazil's default BCP-47 locale. Resource bundles stay locale-aware (S010); this is only the default. */
export const BRAZIL_DEFAULT_LOCALE = 'pt-BR';
/** E.164 country calling code for Brazil (a pack datum, never a universal `+55` assumption). */
export const BRAZIL_CALLING_CODE = '55';
/** Seed epoch: the instant the first Brazil pack version becomes effective. */
export const BRAZIL_PACK_EFFECTIVE_FROM = '2026-01-01T00:00:00Z';
const SEED_ACTOR = 'system:s009-brazil-seed';
/**
 * Universal payment capabilities Brazil enables. These tokens are country-neutral; the mapping from a
 * Brazilian instrument to one of them lives in `BRAZIL_PAYMENT_INSTRUMENTS`.
 */
export const BRAZIL_PAYMENT_CAPABILITIES = ['instant_payment', 'card', 'cash'];
export const BRAZIL_PAYMENT_INSTRUMENTS = [
    { instrument: 'pix', capability: 'instant_payment' },
    { instrument: 'card', capability: 'card' },
    { instrument: 'cash', capability: 'cash' },
];
/**
 * Universal product capabilities Brazil enables — the product lines the country offers, as country-neutral
 * tokens (ADR-0032 D7). Feature availability is expressed at **country** scope with no city/zone tier:
 * `ride`/`moto`/`delivery`/`taxi` are the universal vocabulary, and Brazil currently offers ride-hailing and
 * moto. Extending a country's offering is a new token in this list, never a new geographic dimension.
 *
 * This list is a country-level DECLARATION, not the gate: what a market actually opens in the app (ride, Send, Food) is the governed
 * `product.verticals` setting, whose options are a different vocabulary (`ride` and `moto` here both sit inside the `ride` vertical;
 * `delivery` here covers the `send` and `food` verticals). See docs/architecture/01_MULTI_COUNTRY_OPERATING_MODEL.md §5.1 (D854, LC-census-005).
 */
export const BRAZIL_PRODUCT_CAPABILITIES = ['ride', 'moto'];
export const BRAZIL_DOCUMENT_REQUIREMENTS = [
    { code: 'CPF', holder: 'rider', requirement: 'REQUIRED' },
    { code: 'CPF', holder: 'driver', requirement: 'REQUIRED' },
    { code: 'CNH', holder: 'driver', requirement: 'REQUIRED' },
    { code: 'CRLV', holder: 'vehicle', requirement: 'REQUIRED' },
];
// ---- Policy keys (stable, namespaced) --------------------------------------------------------------
export const BRAZIL_POLICY_KEYS = {
    localeDefault: 'locale.default',
    localeSupported: 'locale.supported',
    moneyCurrency: 'money.currency',
    moneyMinorUnits: 'money.minorUnits',
    unitsDisplayScheme: 'units.displayScheme',
    unitsDisplayDistance: 'units.displayDistance',
    phoneCallingCode: 'phone.callingCode',
    identityDocuments: 'identity.documentTypes',
    paymentCapabilities: 'payment.capabilities',
    paymentInstruments: 'payment.instruments',
    productCapabilities: 'product.capabilities',
    privacyFramework: 'privacy.framework',
    privacyRegulator: 'privacy.regulator',
    regulatoryMobilityBaseline: 'regulatory.mobilityBaseline',
    regulatoryDataTransfer: 'regulatory.dataTransfer',
};
// ---- Country pack (versioned / effective-dated / audited shape) ------------------------------------
/**
 * The Brazil `CountryPack` (S007 shape). Ships as `DRAFT` — approval is a human/ops gate (S014), and a
 * pack existing does not activate any market. Only an `APPROVED`, effective pack governs a transaction
 * (finding #5: a DRAFT pack does NOT resolve). Callers may override `status`/`version`/`effectiveFrom` and,
 * to mint an APPROVED pack (e.g. in a test or the ops approval), supply the `approval` provenance the
 * governance gate requires — all without reaching into universal code.
 */
export function brazilCountryPack(overrides = {}) {
    return countryPack({
        countryCode: BRAZIL,
        version: overrides.version ?? 1,
        status: overrides.status ?? 'DRAFT',
        schemaVersion: overrides.schemaVersion ?? 1,
        effectiveFrom: overrides.effectiveFrom ?? BRAZIL_PACK_EFFECTIVE_FROM,
        ...(overrides.effectiveTo !== undefined ? { effectiveTo: overrides.effectiveTo } : {}),
        currency: BRAZIL_CURRENCY,
        locale: BRAZIL_DEFAULT_LOCALE,
        supportedLocales: [BRAZIL_DEFAULT_LOCALE],
        audit: {
            createdAt: BRAZIL_PACK_EFFECTIVE_FROM,
            createdBy: SEED_ACTOR,
            ...(overrides.approval !== undefined
                ? {
                    approvedBy: overrides.approval.approvedBy,
                    approvalReason: overrides.approval.approvalReason,
                    approvedAt: overrides.approval.approvedAt,
                }
                : {}),
        },
    });
}
// ---- Policy seed (country-scoped settings over the S008 engine) ------------------------------------
const country = (key, value, mode, constraintClass) => policySetting({
    key,
    scope: 'country',
    scopeRef: BRAZIL,
    value,
    version: 1,
    effectiveFrom: BRAZIL_PACK_EFFECTIVE_FROM,
    mode,
    ...(constraintClass ? { constraintClass } : {}),
});
/**
 * The Brazil policy seed: country-scope settings the S008 resolver folds under `platform` and above any
 * region/market/category/geofence/campaign layer. Each declares an override mode so lower scopes stay
 * "more specific, not more powerful": currency is `FIXED`, privacy tightens only, capabilities/documents
 * are additive, and every financial/legal/privacy datum carries its protected constraint class so a
 * campaign can never weaken it.
 */
export function brazilPolicySettings() {
    const K = BRAZIL_POLICY_KEYS;
    return [
        country(K.localeDefault, BRAZIL_DEFAULT_LOCALE, 'OVERRIDABLE'),
        country(K.localeSupported, [BRAZIL_DEFAULT_LOCALE], 'ADDITIVE'),
        country(K.moneyCurrency, BRAZIL_CURRENCY, 'FIXED', 'payment'),
        country(K.moneyMinorUnits, BRAZIL_CURRENCY_MINOR_UNITS, 'FIXED', 'payment'),
        country(K.unitsDisplayScheme, 'metric', 'OVERRIDABLE'),
        country(K.unitsDisplayDistance, 'kilometer', 'OVERRIDABLE'),
        country(K.phoneCallingCode, BRAZIL_CALLING_CODE, 'FIXED'),
        country(K.identityDocuments, BRAZIL_DOCUMENT_REQUIREMENTS, 'ADDITIVE', 'eligibility'),
        country(K.paymentCapabilities, BRAZIL_PAYMENT_CAPABILITIES, 'ADDITIVE', 'payment'),
        country(K.paymentInstruments, BRAZIL_PAYMENT_INSTRUMENTS, 'ADDITIVE', 'payment'),
        country(K.productCapabilities, BRAZIL_PRODUCT_CAPABILITIES, 'ADDITIVE'),
        country(K.privacyFramework, 'LGPD', 'TIGHTEN_ONLY', 'privacy'),
        country(K.privacyRegulator, 'ANPD', 'FIXED', 'privacy'),
        country(K.regulatoryMobilityBaseline, {
            reference: 'Lei 13.640/2018',
            authority: 'Federal',
            requiresMunicipalApproval: true,
        }, 'ADDITIVE', 'legal'),
        country(K.regulatoryDataTransfer, {
            reference: 'ANPD Resolução CD/ANPD nº 19/2024',
            authority: 'ANPD',
        }, 'TIGHTEN_ONLY', 'privacy'),
    ];
}
/** Every key the Brazil seed sets — the natural key set to snapshot for a Brazil transaction context. */
export function brazilPolicyKeys() {
    return Object.values(BRAZIL_POLICY_KEYS);
}
/**
 * Every policy row the Brazil pack publishes: the country-scoped seed, the market-scoped pilot rows that only narrow
 * it (D357: cash-only allowed payment methods in the pilot markets, `./payment-methods.ts`) and the pilot partner-desk
 * rows (D365: the desk's payer set and the categories it sells, `./partner-desk.ts`).
 */
export function brazilPublishedPolicySettings() {
    return [...brazilPolicySettings(), ...brazilPilotPaymentMethodSettings(), ...brazilPartnerDeskSettings()];
}
/**
 * Seed Brazil's policy settings into a durable policy store (ops bring-up). Neutral by
 * construction: this pack owns the Brazil data, and the caller supplies a generic `publish` (e.g.
 * `PolicySettingStore.publish` from `@vello/config-store`), so the **universal API never imports this pack** —
 * it only ever reads the neutral store. Returns the count accepted; a re-run is a no-op (a published version
 * is immutable, so `publish` returns `false`).
 */
export async function publishBrazilPolicies(publish, newId, now) {
    let count = 0;
    for (const setting of brazilPublishedPolicySettings()) {
        if (await publish(setting, newId(), now))
            count += 1;
    }
    return count;
}
// ---- Money + reference market ----------------------------------------------------------------------
/** Construct BRL `Money` from an integer number of centavos (BRL minor units), via the guarded core API. */
export function brazilBrl(centavos) {
    return moneyFromSafeInteger(centavos, BRAZIL_CURRENCY);
}
// ---- First operating market (O003: Paracatu, Minas Gerais) -----------------------------------------
/** Minas Gerais — the administrative region (ISO 3166-2 `BR-MG`) of the first market. */
export const MINAS_GERAIS = regionCode('BR-MG');
/** Typed market id of the first market, Paracatu (`br:paracatu`). */
export const PARACATU_MARKET_ID = marketId('br:paracatu');
/** Geographic center of Paracatu (WGS84) — a universal market attribute, not Brazil-specific data. */
export const PARACATU_CENTER = { latitude: -17.2252, longitude: -46.8711 };
/** The Minas Gerais administrative region record. */
export function minasGeraisRegion() {
    return { code: MINAS_GERAIS, countryCode: BRAZIL, name: 'Minas Gerais' };
}
// ---- Administrative regions: the 26 states + the Federal District (STATE-SCOPE S1, ADR-0124) -------------------
/**
 * Brazil's first-level administrative regions - the 26 states and the Federal District (the "UFs") - as ISO 3166-2:BR codes and Portuguese
 * names. This is the pack's *seed data*: `publishBrazilRegions` registers them into the neutral `RegionStore` (m0225) so the state level of the
 * operating hierarchy (Global > Country > State > City) is DB-registered data, never a literal read on a request path. A market names one of these
 * codes as its `regionCode`; an operator grant can be scoped to one.
 */
const BRAZIL_REGION_NAMES = [
    ['BR-AC', 'Acre'],
    ['BR-AL', 'Alagoas'],
    ['BR-AP', 'Amapá'],
    ['BR-AM', 'Amazonas'],
    ['BR-BA', 'Bahia'],
    ['BR-CE', 'Ceará'],
    ['BR-DF', 'Distrito Federal'],
    ['BR-ES', 'Espírito Santo'],
    ['BR-GO', 'Goiás'],
    ['BR-MA', 'Maranhão'],
    ['BR-MT', 'Mato Grosso'],
    ['BR-MS', 'Mato Grosso do Sul'],
    ['BR-MG', 'Minas Gerais'],
    ['BR-PA', 'Pará'],
    ['BR-PB', 'Paraíba'],
    ['BR-PR', 'Paraná'],
    ['BR-PE', 'Pernambuco'],
    ['BR-PI', 'Piauí'],
    ['BR-RJ', 'Rio de Janeiro'],
    ['BR-RN', 'Rio Grande do Norte'],
    ['BR-RS', 'Rio Grande do Sul'],
    ['BR-RO', 'Rondônia'],
    ['BR-RR', 'Roraima'],
    ['BR-SC', 'Santa Catarina'],
    ['BR-SP', 'São Paulo'],
    ['BR-SE', 'Sergipe'],
    ['BR-TO', 'Tocantins'],
];
/** Brazil's 27 UFs as typed `AdministrativeRegion`s (every code passes `regionCode()`; every one is `BR`'s own). */
export function brazilRegions() {
    return BRAZIL_REGION_NAMES.map(([code, name]) => ({ code: regionCode(code), countryCode: BRAZIL, name }));
}
/**
 * Seed Brazil's administrative regions into a durable region catalog (ops bring-up). Same neutral boundary as `publishBrazilMarkets`: the pack owns
 * the Brazil data and the caller supplies a generic `upsert` (e.g. `RegionStore.upsert` from `@vello/config-store`), so the universal API never
 * imports this pack. Idempotent - `upsert` reports `CREATED`, `UPDATED` (the real name over the placeholder m0225's backfill wrote) or `UNCHANGED`.
 * Returns the count written (created + updated); a re-run writes nothing.
 */
export async function publishBrazilRegions(upsert, now) {
    let count = 0;
    for (const region of brazilRegions()) {
        if ((await upsert(region, now)) !== 'UNCHANGED')
            count += 1;
    }
    return count;
}
/** The first operating market as a typed `OperatingMarket` (Paracatu, MG, `America/Sao_Paulo`). */
export function brazilFirstMarket() {
    return operatingMarket({
        id: PARACATU_MARKET_ID,
        countryCode: BRAZIL,
        regionCode: MINAS_GERAIS,
        name: 'Paracatu',
        timezone: 'America/Sao_Paulo',
        center: PARACATU_CENTER,
    });
}
// ---- Second pilot market (D353 owner decision 4: Patos de Minas, Minas Gerais) -----------------------
/** Typed market id of the second pilot market, Patos de Minas (`br:patos-de-minas`). */
export const PATOS_DE_MINAS_MARKET_ID = marketId('br:patos-de-minas');
/** Geographic center of Patos de Minas (WGS84, approximate city center). */
export const PATOS_DE_MINAS_CENTER = { latitude: -18.5789, longitude: -46.5181 };
/** The second pilot market as a typed `OperatingMarket` (Patos de Minas, MG, `America/Sao_Paulo`). */
export function brazilPatosDeMinasMarket() {
    return operatingMarket({
        id: PATOS_DE_MINAS_MARKET_ID,
        countryCode: BRAZIL,
        regionCode: MINAS_GERAIS,
        name: 'Patos de Minas',
        timezone: 'America/Sao_Paulo',
        center: PATOS_DE_MINAS_CENTER,
    });
}
/** The pilot markets of the first cycle (D353): Paracatu and Patos de Minas, both in Minas Gerais. */
export const BRAZIL_PILOT_MARKET_IDS = [PARACATU_MARKET_ID, PATOS_DE_MINAS_MARKET_ID];
/**
 * The first market ships `DRAFT`, never `ACTIVE`. A city becomes live only after the S014 launch gates —
 * municipal authorization, jurisdiction coverage, and the readiness sign-off — not because seed data
 * mentions it. Seeding Paracatu records the decision; it does not activate the city.
 */
export function brazilFirstMarketLaunchState() {
    return marketLaunchState(PARACATU_MARKET_ID, 'DRAFT', BRAZIL_PACK_EFFECTIVE_FROM);
}
/** BR country → Minas Gerais → Paracatu, shaped to pass `validateHierarchy` from `@vello/config`. */
export function brazilRegistry() {
    return {
        countries: [{ code: BRAZIL, name: 'Brazil' }],
        regions: brazilRegions(),
        markets: brazilMarkets(),
    };
}
/**
 * Brazil's declared operating markets (cities). Today: the two pilot markets, Paracatu and Patos de Minas (MG). This is the pack's *seed data* — the list
 * of markets Brazil declares — not the served source of truth: `publishBrazilMarkets` registers them into the
 * neutral `MarketStore` (m0101) so a city becomes DB-registered data the platform serves, never a hardcoded
 * literal read on a request path (owner directive 2026-09-03: "nada era para ser em hardcode"). Adding a Brazil
 * city is a new entry here + a re-run of the seed, exactly as BRL/pt-BR are pack data published into the country
 * config on approve.
 */
export function brazilMarkets() {
    return [brazilFirstMarket(), brazilPatosDeMinasMarket()];
}
/**
 * Seed Brazil's operating markets into a durable market registry (ops bring-up). Neutral by construction: this
 * pack owns the Brazil market data, and the caller supplies a generic `register` (e.g. `MarketStore.register`
 * from `@vello/config-store`), so the **universal API never imports this pack** — it only ever reads the neutral
 * store/route. Markets are seeded `DRAFT` (a city is never born live; activation is the launch gate). Returns the
 * count newly registered; a re-run is a no-op for markets that already exist (`register` returns `false`).
 */
export async function publishBrazilMarkets(register, now) {
    let count = 0;
    for (const market of brazilMarkets()) {
        if (await register(market, 'DRAFT', now))
            count += 1;
    }
    return count;
}
//# sourceMappingURL=brazil.js.map